import type { ExcelCell, IMenuActionParams } from "ag-grid-community";

import { defaultSelector } from "../copyRangeSelectionHelpers";
import type { TColumnHeaderProps } from "../components";
import type { GetContextMenuSection } from "./helpers";
import { getIcon } from "./icons";
import { uomToLabel } from "../../utils";
import { captureEvent } from "../../../utils/o11y";
import { useActivePageId, usePage } from "../../../data";

export const useGetMenuItemsExportData = (): GetContextMenuSection => {
  const pageId = useActivePageId();
  const page = usePage(pageId);
  const pageName = page.data?.name || "export";

  return (_ctx) => {
    return [
      {
        name: "Export",
        icon: getIcon("export"),
        subMenu: [
          {
            name: "Export without Metadata",
            action: (params) => exportToExcelWithoutMetadata(params, pageName),
          },
          {
            name: "Export with Metadata",
            action: (params) => exportToExcelWithMetadata(params, pageName),
          },
        ],
      },
    ];
  };
};

export function exportToExcelWithoutMetadata(params: IMenuActionParams, pageName: string) {
  captureEvent("clicked_export_to_excel_without_metadata");

  const api = params.api;
  if (!api) throw new Error("No grid api provided");

  const columnDefs = api?.getColumns()?.map((column) => column?.getColDef());

  const headers = columnDefs?.map((column) => {
    const columnParams: TColumnHeaderProps = column.headerComponentParams;
    const selector = defaultSelector(columnParams?.artisType || "");

    return {
      packageName: column.headerName,
      ...columnParams,
      selector,
    };
  });

  const data = headers?.map((header) => {
    const labels = header.columnLabels;
    const isPeriodColumn = header?.packageName?.toLowerCase() === "period";
    const isAuxiliaryColumn = !!header?.shadowType;
    const hasLabels = !!(labels?.description || labels?.uom || labels?.uomDisplay);

    return {
      name: header?.packageName,
      description: isPeriodColumn || isAuxiliaryColumn || !hasLabels ? "" : labels?.description,
      uom:
        isPeriodColumn || isAuxiliaryColumn || !hasLabels
          ? ""
          : uomToLabel({
              cellLabel: labels?.uom,
              uomDescription: labels?.uomDisplay,
              selector: header?.field,
            }),
    };
  });

  const names = data?.map((header) => cell(header?.name?.toString() || "")) || [];
  const descriptions = data?.map((header) => cell(header?.description?.toString() || "")) || [];
  const uoms = data?.map((header) => cell(header?.uom?.toString() || "")) || [];

  const content = [names, descriptions, uoms].map((row) => {
    return { cells: row };
  });

  const rowsNeedingBlankAfter = new Set<string>();
  let prevRowType: string | null = null;

  api.forEachNode((node) => {
    const currentRowType = node.data?.rowType;
    if (prevRowType !== null && currentRowType !== prevRowType) {
      const prevNodeIndex = node.rowIndex !== null ? node.rowIndex - 1 : -1;
      if (prevNodeIndex >= 0) {
        const prevNode = api.getDisplayedRowAtIndex(prevNodeIndex);
        if (prevNode?.id) {
          rowsNeedingBlankAfter.add(prevNode.id);
        }
      }
    }
    prevRowType = currentRowType;
  });

  const columnCount = columnDefs?.length || 0;
  const blankRow = {
    cells: Array(columnCount).fill(cell("")),
  };

  api?.exportDataAsExcel({
    skipColumnHeaders: true,
    skipColumnGroupHeaders: true,
    prependContent: content,
    getCustomContentBelowRow: (params) => {
      if (params.node.id && rowsNeedingBlankAfter.has(params.node.id)) {
        return [blankRow];
      }
      return undefined;
    },
    fileName: `${pageName}.xlsx`,
    sheetName: pageName,
  });
}

export function exportToExcelWithMetadata(params: IMenuActionParams, pageName: string) {
  captureEvent("clicked_export_to_excel");

  const api = params.api;
  if (!api) throw new Error("No grid api provided");

  const columnDefs = api?.getColumns()?.map((column) => column?.getColDef());

  const headers = columnDefs?.map((column) => {
    const columnParams: TColumnHeaderProps = column.headerComponentParams;
    const selector = defaultSelector(columnParams?.artisType || "");

    return {
      packageName: column.headerName,
      ...columnParams,
      selector,
    };
  });

  const data = headers?.map((header) => {
    const labels = header.columnLabels;
    const isPeriodColumn = header?.packageName?.toLowerCase() === "period";
    const isAuxiliaryColumn = !!header?.shadowType;
    const hasLabels = !!(labels?.description || labels?.uom || labels?.uomDisplay);

    return {
      name: header?.packageName,
      description: isPeriodColumn || isAuxiliaryColumn ? "" : labels?.description,
      productId: isPeriodColumn || isAuxiliaryColumn ? "" : header?.productId,
      package: isPeriodColumn || isAuxiliaryColumn ? "" : labels?.package,
      uom:
        isPeriodColumn || isAuxiliaryColumn || !hasLabels
          ? ""
          : uomToLabel({
              cellLabel: labels?.uom,
              uomDescription: labels?.uomDisplay,
              selector: header?.field,
            }),
      selector: isPeriodColumn || isAuxiliaryColumn ? undefined : header?.selector,
    };
  });

  const names = data?.map((header) => cell(header?.name?.toString() || "")) || [];
  const descriptions = data?.map((header) => cell(header?.description?.toString() || "")) || [];
  const productIds = data?.map((header) => cell(header?.productId?.toString() || "")) || [];
  const packages = data?.map((header) => cell(header?.package?.toString() || "")) || [];
  const uoms = data?.map((header) => cell(header?.uom?.toString() || "")) || [];
  const fieldSelectors = data?.map((header) => cell(header?.selector?.toString() || "")) || [];

  const content = [names, descriptions, productIds, packages, uoms, fieldSelectors].map((row) => {
    return { cells: row };
  });

  const rowsNeedingBlankAfter = new Set<string>();
  let prevRowType: string | null = null;

  api.forEachNode((node) => {
    const currentRowType = node.data?.rowType;
    if (prevRowType !== null && currentRowType !== prevRowType) {
      const prevNodeIndex = node.rowIndex !== null ? node.rowIndex - 1 : -1;
      if (prevNodeIndex >= 0) {
        const prevNode = api.getDisplayedRowAtIndex(prevNodeIndex);
        if (prevNode?.id) {
          rowsNeedingBlankAfter.add(prevNode.id);
        }
      }
    }
    prevRowType = currentRowType;
  });

  const columnCount = columnDefs?.length || 0;
  const blankRow = {
    cells: Array(columnCount).fill(cell("")),
  };

  api?.exportDataAsExcel({
    skipColumnHeaders: true,
    skipColumnGroupHeaders: true,
    prependContent: content,
    getCustomContentBelowRow: (params) => {
      if (params.node.id && rowsNeedingBlankAfter.has(params.node.id)) {
        return [blankRow];
      }
      return undefined;
    },
    fileName: `${pageName}.xlsx`,
    sheetName: pageName,
  });
}

function cell(value: string): ExcelCell {
  return { data: { value, type: "String" } };
}
